"use client";

import React, { useEffect, useRef, useState } from "react";

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
}

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0 text-gray-400">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <path d="M20 20l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    className={`shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(query.trim().toLowerCase())
  );

  const select = (option: string) => {
    onChange(option);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger / search box */}
      <div
        className="flex items-center gap-2 w-full p-3 border border-gray-200 rounded-xl bg-white shadow-sm cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <>
            <SearchIcon />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={placeholder}
              className="flex-1 min-w-0 outline-none bg-transparent text-gray-900 placeholder:text-gray-400"
            />
          </>
        ) : (
          <span className={`flex-1 min-w-0 truncate ${value ? "text-gray-900" : "text-gray-400"}`}>
            {value || placeholder}
          </span>
        )}
        <Chevron open={open} />
      </div>

      {/* Options panel */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 z-20 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-5 py-4 text-gray-400 text-sm">Ничего не найдено</div>
            ) : (
              filtered.map((option, idx) => {
                const selected = option === value;
                return (
                  <button
                    type="button"
                    key={option}
                    onClick={() => select(option)}
                    className={`flex items-center justify-between w-full px-5 py-4 text-left transition-colors hover:bg-gray-50 ${
                      idx !== filtered.length - 1 ? "border-b border-gray-100" : ""
                    } ${selected ? "bg-gray-50" : ""}`}
                  >
                    <span className={selected ? "text-blue-600" : "text-gray-900"}>
                      {option}
                    </span>
                    {selected && (
                      <span className="w-2.5 h-2.5 rounded-full bg-blue-600 shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
